import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { Star, ArrowRight, ArrowDown, Crosshair, Hammer, Shapes, MapPin } from "lucide-react";
import ChapterHeading from "@/components/ChapterHeading";
import HowItWorks from "@/components/HowItWorks";
import AssessmentFAQ from "@/components/AssessmentFAQ";
import ImageReveal from "@/components/ImageReveal";
import Reveal from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import CTABand from "@/components/CTABand";
import MultiRailPromo from "@/components/MultiRailPromo";
import { QuoteButton, CallButton } from "@/components/Buttons";
import usePageMeta from "@/hooks/usePageMeta";
import useReviews from "@/hooks/useReviews";
import { useContent } from "@/content/ContentContext";
import { GOOGLE_PROFILE_URL, GOOGLE_RATING, MANUAL_REVIEWS } from "@/data/reviews";

const SERVICES = [
  {
    num: "01",
    title: "Grab Rails",
    copy: "Professional grab rail supply and installation for bathrooms, showers, toilets and other areas around the home where additional support may be useful.",
    img: "/images/grab-05.jpg",
    alt: "Grab rails installed for a senior in Perth to support independent living",
    cta: "EXPLORE GRAB RAILS",
    to: "/grab-rails",
    testid: "service-card-grab-rails",
  },
  {
    num: "02",
    title: "Hand Rails",
    copy: "Practical hand rail solutions for stairs, entrances, pathways, steps and other areas where additional support is required.",
    img: "/images/hand-04.jpg",
    alt: "Custom modular handrail installation in Perth, Western Australia",
    cta: "EXPLORE HAND RAILS",
    to: "/hand-rails",
    testid: "service-card-hand-rails",
  },
  {
    num: "03",
    title: "Rubber Ramps",
    copy: "Practical rubber ramp solutions for suitable steps, thresholds and changes in level.",
    img: "/images/ramp-06.jpg",
    alt: "Rubber ramp fitted at a doorway threshold in Perth",
    cta: "EXPLORE RUBBER RAMPS",
    to: "/rubber-ramps",
    testid: "service-card-rubber-ramps",
  },
];

const BENEFITS = [
  {
    icon: Crosshair,
    title: "Specialist Focus",
    copy: "We focus specifically on practical safety and accessibility installations.",
  },
  {
    icon: Hammer,
    title: "Professional Installation",
    copy: "Quality products installed carefully and professionally.",
  },
  {
    icon: Shapes,
    title: "Practical Solutions",
    copy: "Solutions designed around the space and the way it is used.",
  },
  {
    icon: MapPin,
    title: "Perth Based",
    copy: "Providing local service across Perth.",
  },
];

const PREVIEW_IMAGES = [
  { src: "/images/grab-01.jpg", alt: "Grab rail installed in a Perth bathroom to improve safety and support at home", span: "col-span-2 row-span-2" },
  { src: "/images/hand-04.jpg", alt: "Custom modular handrail installation in Perth, Western Australia", span: "" },
  { src: "/images/ramp-02.jpg", alt: "Rubber threshold ramp installed at a Perth home entry", span: "" },
  { src: "/images/grab-04.jpg", alt: "Shower grab rail installed in a Perth home to reduce slip and fall risk", span: "" },
  { src: "/images/hand-10.jpg", alt: "Modular handrail supplied and installed in Perth", span: "" },
  { src: "/images/ramp-07.jpg", alt: "Rubber ramp installed in Mandurah, Western Australia", span: "col-span-2" },
  { src: "/images/grab-08.jpg", alt: "Grab rail installed in a Joondalup bathroom to improve home safety", span: "" },
  { src: "/images/ramp-01.jpg", alt: "Rubber ramp installed in Mandurah, Perth, Western Australia", span: "" },
];

function Stars({ size = "h-5 w-5", count = 5 }) {
  return (
    <span className="flex items-center gap-1" role="img" aria-label={`${count} star rating`}>
      {[...Array(Math.max(1, Math.min(5, count)))].map((_, i) => (
        <Star key={i} className={`${size} fill-gold text-gold`} aria-hidden="true" />
      ))}
    </span>
  );
}

export { Stars };

export default function Home() {
  const { get } = useContent();
  const reviewsData = useReviews();
  usePageMeta(
    get("meta_home_title", "Grab Rails, Hand Rails & Rubber Ramps Perth | Modify"),
    get(
      "meta_home_desc",
      "Specialist grab rail, hand rail and rubber ramp supply and professional installation across Perth, WA. We care about making everyday living safer. Free quotes."
    )
  );
  const reduce = useReducedMotion();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 90]);

  const headline = [get("home_hero_line1", "WE CARE ABOUT"), get("home_hero_line2", "SAFER LIVING.")];
  const liveRating = reviewsData?.configured && reviewsData?.rating;

  return (
    <div data-testid="home-page">
      <section ref={heroRef} data-testid="hero-section" className="relative overflow-hidden bg-charcoal text-ivory">
        <div
          className="pointer-events-none absolute -right-40 -top-40 h-[480px] w-[480px] rounded-full bg-sage/20 blur-[120px]"
          aria-hidden="true"
        />
        <div className="relative mx-auto max-w-7xl px-4 pb-16 pt-44 sm:px-6 lg:px-8 lg:pb-28 lg:pt-40">
          <div className="grid items-center gap-14 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <motion.div
                initial={reduce ? { opacity: 0 } : { opacity: 0, y: 16 }}
                animate={reduce ? { opacity: 1 } : { opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="mb-8 hidden lg:block"
              >
                <img
                  src="/images/logo-white.png"
                  alt="Modify — We Care"
                  data-testid="home-hero-logo"
                  className="w-64"
                />
              </motion.div>

              <motion.div
                initial={reduce ? { opacity: 0 } : { opacity: 0, y: 16 }}
                animate={reduce ? { opacity: 1 } : { opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="inline-flex items-center gap-3 rounded-full border border-white/15 px-4 py-2"
              >
                <span className="h-2 w-2 rounded-full bg-sage-tint" aria-hidden="true" />
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-ivory/80">
                  Specialist Installation — Perth, WA
                </span>
              </motion.div>

              <h1 className="mt-7 font-display text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
                {headline.map((line, i) => (
                  <span key={line} className="block overflow-hidden pb-1">
                    <motion.span
                      className={`block ${i === 1 ? "text-sage-tint" : ""}`}
                      initial={reduce ? { opacity: 0 } : { y: "110%" }}
                      animate={reduce ? { opacity: 1 } : { y: 0 }}
                      transition={{ delay: 0.2 + i * 0.16, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                    >
                      {line}
                    </motion.span>
                  </span>
                ))}
              </h1>

              <motion.p
                initial={reduce ? { opacity: 0 } : { opacity: 0, y: 16 }}
                animate={reduce ? { opacity: 1 } : { opacity: 1, y: 0 }}
                transition={{ delay: 0.55, duration: 0.7 }}
                className="mt-6 max-w-xl text-base leading-relaxed text-ivory/75 sm:text-lg"
              >
                {get("home_hero_sub", "Grab rails, hand rails and rubber ramps — supplied and professionally installed across Perth.")}
              </motion.p>

              <motion.div
                initial={reduce ? { opacity: 0 } : { opacity: 0, y: 16 }}
                animate={reduce ? { opacity: 1 } : { opacity: 1, y: 0 }}
                transition={{ delay: 0.7, duration: 0.7 }}
                className="mt-9 flex flex-col gap-3 sm:flex-row"
              >
                <QuoteButton variant="light" testid="hero-quote-button" />
                <CallButton variant="outline-light" />
              </motion.div>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.9, duration: 0.7 }}
                className="mt-7 text-sm font-semibold text-ivory/55"
              >
                Simple solutions. Professional installation.
              </motion.p>
            </div>

            <div className="lg:col-span-5">
              <motion.div
                style={{ y: imgY }}
                initial={reduce ? { opacity: 0 } : { opacity: 0, y: 40 }}
                animate={reduce ? { opacity: 1 } : { opacity: 1, y: 0 }}
                transition={{ delay: 0.35, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                className="relative"
              >
                <div className="spotlight-frame overflow-hidden rounded-[2rem]">
                  <img
                    src="/images/grab-hero.jpg"
                    alt="Professionally installed grab rails in a Perth bathroom"
                    className="aspect-[4/5] w-full object-cover sm:aspect-[5/5]"
                    loading="eager"
                    fetchPriority="high"
                  />
                </div>
                <div className="absolute -bottom-6 -left-4 flex items-center gap-3 rounded-2xl bg-white p-4 shadow-2xl sm:-left-8">
                  <img src="/images/logo.png" alt="" className="h-10 w-10 rounded-lg" aria-hidden="true" />
                  <span>
                    <span className="block font-display text-sm font-bold text-charcoal">Specialist Installation</span>
                    <span className="block text-xs font-semibold text-ink/60">
                      Grab rails · Hand rails · Rubber ramps
                    </span>
                  </span>
                </div>
              </motion.div>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2 }}
            className="mt-20 hidden justify-center lg:flex"
            aria-hidden="true"
          >
            <ArrowDown className="h-5 w-5 animate-bounce text-ivory/40" />
          </motion.div>
        </div>
      </section>

      <section data-testid="trust-bar" className="border-b border-line bg-ivory">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-center gap-3 px-4 py-6 text-center sm:flex-row sm:gap-4 sm:px-6 lg:px-8">
          <Stars count={liveRating ? Math.round(reviewsData.rating) : 5} />
          <p className="text-sm font-bold uppercase tracking-[0.15em] text-charcoal">
            {liveRating ? `${reviewsData.rating} Google rating` : `${GOOGLE_RATING.toFixed(1)} Google rating`}
          </p>
          <span className="hidden text-ink/30 sm:inline" aria-hidden="true">
            ·
          </span>
          <p className="text-sm font-semibold text-ink/60">
            A 5-star service on Google
          </p>
        </div>
      </section>

      <Marquee />

      <section data-testid="services-section" className="bg-ivory">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8 lg:py-32">
          <ChapterHeading
            num="01"
            eyebrow="What we specialise in"
            title="Three services. One focus: safer living."
            copy="Modify supplies and professionally installs grab rails, hand rails and rubber ramps across Perth — nothing else. That focus means practical advice and careful installation, every time."
            testid="services-heading"
          />
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {SERVICES.map((service, i) => (
              <Reveal key={service.title} delay={i * 0.08}>
                <Link
                  to={service.to}
                  data-testid={service.testid}
                  className="group flex h-full flex-col overflow-hidden rounded-3xl border border-line bg-white transition-colors duration-300 hover:border-sage"
                >
                  <ImageReveal className="overflow-hidden" delay={i * 0.08} radius="0px">
                    <img
                      src={service.img}
                      alt={service.alt}
                      loading="lazy"
                      className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </ImageReveal>
                  <div className="flex flex-1 flex-col p-7">
                    <span className="text-xs font-bold tracking-[0.2em] text-sage-dark">{service.num}</span>
                    <h3 className="mt-2 font-display text-2xl font-bold text-charcoal">{service.title}</h3>
                    <p className="mt-3 flex-1 text-sm leading-relaxed text-ink/70">{service.copy}</p>
                    <span className="mt-6 inline-flex items-center gap-2 text-xs font-bold tracking-[0.12em] text-charcoal transition-colors duration-200 group-hover:text-sage-dark">
                      {service.cta}
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <HowItWorks />

      <section data-testid="why-modify-section" className="border-y border-line bg-white">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8 lg:py-32">
          <ChapterHeading
            num="03"
            eyebrow="Why Modify"
            title="Simple safety solutions. Professionally installed."
            copy="Modify focuses specifically on grab rails, hand rails and rubber ramps, allowing us to provide a more focused and practical service for customers looking to improve safety and accessibility around the home."
            testid="why-modify-heading"
          />
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {BENEFITS.map((benefit, i) => (
              <Reveal key={benefit.title} delay={i * 0.07}>
                <div className="group h-full rounded-3xl border border-line bg-ivory p-7 transition-colors duration-300 hover:border-sage">
                  <benefit.icon className="h-7 w-7 text-sage-dark" aria-hidden="true" />
                  <h3 className="mt-5 font-display text-lg font-bold uppercase tracking-wide text-charcoal">
                    {benefit.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink/70">{benefit.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="grab-rail-feature-section" className="bg-charcoal text-ivory">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8 lg:py-32">
          <div className="grid items-center gap-12 lg:grid-cols-12">
            <Reveal className="lg:col-span-6">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-sage-tint">Grab rails</p>
              <h2 className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl lg:text-5xl">
                NEED A GRAB RAIL INSTALLED?
              </h2>
              <p className="mt-5 max-w-lg text-base leading-relaxed text-ivory/75 sm:text-lg">
                Tell us what you need, send us a photo, and we'll help you find the right solution.
              </p>
              <div className="mt-8">
                <QuoteButton
                  label="GET INSTALLED TODAY"
                  service="Grab Rails"
                  variant="light"
                  testid="grab-feature-quote-button"
                />
              </div>
            </Reveal>
            <ImageReveal delay={0.1} radius="2rem" className="spotlight-frame overflow-hidden rounded-[2rem] lg:col-span-6">
              <img
                src="/images/grab-wide.jpg"
                alt="Accessible bathroom grab rails installed for a Perth home"
                loading="lazy"
                className="aspect-[16/10] w-full object-cover"
              />
            </ImageReveal>
          </div>
        </div>
      </section>

      <MultiRailPromo variant="compact" />

      <section data-testid="gallery-preview-section" className="bg-ivory">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8 lg:py-32">
          <ChapterHeading
            num="04"
            eyebrow="Our recent work"
            title="Installed in real Perth homes."
            testid="gallery-preview-heading"
          />
          <div className="mt-14 grid auto-rows-[160px] grid-cols-2 gap-4 sm:auto-rows-[200px] md:grid-cols-4">
            {PREVIEW_IMAGES.map((img, i) => (
              <ImageReveal key={img.src} delay={i * 0.05} radius="1rem" className={img.span}>
                <Link
                  to="/gallery"
                  data-testid={`gallery-preview-image-${i}`}
                  className="group block h-full w-full overflow-hidden rounded-2xl border border-line"
                  aria-label={`${img.alt} — open gallery`}
                >
                  <img
                    src={img.src}
                    alt={img.alt}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </Link>
              </ImageReveal>
            ))}
          </div>
          <Reveal className="mt-12 text-center">
            <Link
              to="/gallery"
              data-testid="view-gallery-button"
              className="inline-flex min-h-[48px] items-center gap-2 rounded-full border-2 border-charcoal px-8 py-3 text-sm font-bold tracking-[0.12em] text-charcoal transition-colors duration-300 hover:bg-charcoal hover:text-white"
            >
              VIEW OUR GALLERY
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section data-testid="home-reviews-section" className="border-y border-line bg-white">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8 lg:py-32">
          <ChapterHeading
            num="05"
            eyebrow="What our customers say"
            title="5-star service, Perth-wide."
            testid="home-reviews-heading"
          />
          <Reveal delay={0.1}>
            <div className="mt-12 rounded-3xl border border-line bg-ivory p-8 sm:p-12">
              <div className="flex flex-wrap items-center gap-4">
                <span className="font-display text-5xl font-extrabold text-charcoal">{GOOGLE_RATING.toFixed(1)}</span>
                <div>
                  <Stars size="h-6 w-6" count={5} />
                  <p className="mt-2 text-sm font-semibold text-ink/60">
                    Genuine Google reviews from Perth customers
                  </p>
                </div>
              </div>
              <div className="mt-8 grid gap-5 md:grid-cols-3">
                {MANUAL_REVIEWS.slice(0, 3).map((review, i) => (
                  <div key={review.author} data-testid={`home-review-card-${i}`} className="flex h-full flex-col rounded-2xl border border-line bg-white p-6">
                    <Stars size="h-4 w-4" count={review.rating} />
                    <p className="mt-3 flex-1 text-sm leading-relaxed text-ink/80">{review.text}</p>
                    <div className="mt-4 border-t border-line pt-3">
                      <p className="font-display text-sm font-bold text-charcoal">{review.author}</p>
                      <p className="mt-0.5 text-xs text-ink/50">{review.time} · Google review</p>
                    </div>
                  </div>
                ))}
              </div>
              <Link
                to="/reviews"
                data-testid="see-all-reviews-button"
                className="mt-8 inline-flex min-h-[48px] items-center gap-2 rounded-full bg-ember px-7 py-3 text-sm font-bold tracking-[0.08em] text-charcoal transition-colors duration-300 hover:bg-ember-dark hover:text-white"
              >
                SEE ALL REVIEWS
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section data-testid="about-section" className="bg-ivory">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24 lg:px-8 lg:py-32">
          <div className="grid items-center gap-12 lg:grid-cols-12">
            <ImageReveal radius="2rem" className="overflow-hidden rounded-[2rem] border border-line lg:col-span-5">
              <img
                src="/images/grab-09.jpg"
                alt="Grab rails installed in a Morley bathroom to reduce fall risk at home"
                loading="lazy"
                className="aspect-[4/5] w-full object-cover"
              />
            </ImageReveal>
            <div className="lg:col-span-7">
              <ChapterHeading
                num="06"
                eyebrow="We care"
                title="More than a rail on a wall."
                testid="about-heading"
              />
              <Reveal delay={0.1}>
                <div className="mt-6 space-y-5 text-base leading-relaxed text-ink/75 sm:text-lg">
                  <p>{get("home_about_p1", "Modify is a Perth-based specialist focused on one thing: making everyday living safer with grab rails, hand rails and rubber ramps.")}</p>
                  <p>{get("home_about_p2", "We understand that installing a grab rail or hand rail is rarely just about hardware. It's about feeling confident and independent in your own home — stepping into the shower without worry, or getting through the front door with ease.")}</p>
                  <p className="font-display text-xl font-bold text-charcoal">
                    {get("home_about_p3", "Practical safety solutions for greater confidence at home. That's what we care about.")}
                  </p>
                </div>
                <div className="mt-8">
                  <QuoteButton testid="about-quote-button" />
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <AssessmentFAQ />

      <CTABand
        title={get("home_final_title", "MAKE YOUR HOME SAFER TODAY.")}
        copy={get("home_final_copy", "From grab rails and hand rails to practical rubber ramp solutions, Modify provides simple safety solutions with professional installation across Perth.")}
        testid="final-cta-section"
      />
    </div>
  );
}
